<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useWindowSize } from '@vueuse/core';

import ContactListHeaderWrapper from 'dashboard/components-next/Contacts/ContactsHeader/ContactListHeaderWrapper.vue';
import ContactsActiveFiltersPreview from 'dashboard/components-next/Contacts/ContactsHeader/components/ContactsActiveFiltersPreview.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import ContactsLoadMore from 'dashboard/components-next/Contacts/ContactsLoadMore.vue';
import ContactsIndexSidebar from 'dashboard/components-next/Contacts/ContactsIndexSidebar.vue';

const props = defineProps({
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  showPaginationFooter: { type: Boolean, default: true },
  currentPage: { type: Number, default: 1 },
  totalItems: { type: Number, default: 100 },
  itemsPerPage: { type: Number, default: 15 },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
  activeSegment: { type: Object, default: null },
  segmentsId: { type: [String, Number], default: 0 },
  hasAppliedFilters: { type: Boolean, default: false },
  isFetchingList: { type: Boolean, default: false },
  useInfiniteScroll: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  isLoadingMore: { type: Boolean, default: false },
  activeSidebarFilter: { type: String, default: '' },
});

const emit = defineEmits([
  'update:currentPage',
  'update:sort',
  'search',
  'applyFilter',
  'applySidebarFilter',
  'clearFilters',
  'selectAllContacts',
  'loadMore',
]);

const route = useRoute();
const { width: windowWidth } = useWindowSize();

const contactListHeaderWrapper = ref(null);
const isSidebarCollapsed = ref(false);

const isDesktopContactsChrome = computed(() => windowWidth.value >= 768);

const isNotSegmentView = computed(() => {
  return route.name !== 'contacts_dashboard_segments_index';
});

const isActiveView = computed(() => {
  return route.name === 'contacts_dashboard_active';
});

const isLabelView = computed(
  () => route.name === 'contacts_dashboard_labels_index'
);

const showActiveFiltersPreview = computed(() => {
  return (
    (props.hasAppliedFilters || !isNotSegmentView.value) &&
    !props.isFetchingList &&
    !isLabelView.value &&
    !isActiveView.value
  );
});

const updateCurrentPage = page => {
  emit('update:currentPage', page);
};

const openFilter = () => {
  contactListHeaderWrapper.value?.onToggleFilters();
};

const showLoadMore = computed(() => {
  return props.useInfiniteScroll && props.hasMore;
});

const showPagination = computed(() => {
  return !props.useInfiniteScroll && props.showPaginationFooter;
});
</script>

<template>
  <section
    class="flex h-full w-full min-w-0 gap-0 overflow-hidden bg-n-surface-1"
  >
    <div
      v-if="isDesktopContactsChrome && !isSidebarCollapsed"
      class="hidden h-full min-h-0 w-[218px] shrink-0 md:flex"
    >
      <ContactsIndexSidebar
        class="h-full min-h-0 w-full"
        :total-items="totalItems"
        :has-applied-filters="hasAppliedFilters"
        :active-sidebar-filter="activeSidebarFilter"
        @collapse="isSidebarCollapsed = true"
        @apply-filter="emit('applySidebarFilter', $event)"
        @select-all="emit('selectAllContacts')"
      />
    </div>

    <div
      class="flex h-full min-h-0 min-w-0 flex-1 flex-col transition-all duration-300"
    >
      <ContactListHeaderWrapper
        ref="contactListHeaderWrapper"
        :show-search="isNotSegmentView && !isActiveView"
        :search-value="searchValue"
        :active-sort="activeSort"
        :active-ordering="activeOrdering"
        :header-title="headerTitle"
        :active-segment="activeSegment"
        :segments-id="segmentsId"
        :has-applied-filters="hasAppliedFilters"
        :is-label-view="isLabelView"
        :is-active-view="isActiveView"
        :sidebar-collapsed="isSidebarCollapsed && isDesktopContactsChrome"
        @update:sort="emit('update:sort', $event)"
        @search="emit('search', $event)"
        @apply-filter="emit('applyFilter', $event)"
        @clear-filters="emit('clearFilters')"
        @expand-sidebar="isSidebarCollapsed = false"
      />
      <main class="flex min-h-0 flex-1 flex-col overflow-hidden px-4">
        <div class="flex min-h-0 w-full flex-1 flex-col">
          <ContactsActiveFiltersPreview
            v-if="showActiveFiltersPreview"
            :active-segment="activeSegment"
            class="mb-1 shrink-0"
            @clear-filters="emit('clearFilters')"
            @open-filter="openFilter"
          />
          <slot name="default" />
          <ContactsLoadMore
            v-if="showLoadMore"
            :is-loading="isLoadingMore"
            class="shrink-0"
            @load-more="emit('loadMore')"
          />
        </div>
      </main>
      <footer v-if="showPagination" class="sticky bottom-0 z-0 shrink-0">
        <PaginationFooter
          current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
          :current-page="currentPage"
          :total-items="totalItems"
          :items-per-page="itemsPerPage"
          @update:current-page="updateCurrentPage"
        />
      </footer>
    </div>
  </section>
</template>
